import { Card } from "primereact/card";
import { Image } from "primereact/image";
import { useState } from "react";
import { infoToast, Spinner } from "@idwal/idwal-react-components";
import { IServiceEvent } from "../types/IServiceEvent";
import { IService } from "../types/IService";
import { IParentService } from "../types/IParentService";
import { ServiceActionModal } from "./ServiceActionModal";
import { useNavigate } from "react-router-dom";
import { EventCardFooter } from "./EventCardFooter";
import { Button } from "primereact/button";
import { RetryService } from "../services/RetryService";

/**
 * Service card component
 */
export const ServiceCard = (props: {
    parentService: IParentService;
    service: IService;
    events: IServiceEvent[];
    errors: IServiceEvent[];
}) => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const [openEvent, setOpenEvent] = useState<IServiceEvent | null>(null);
    const navigate = useNavigate();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function openEventInDialog(e: any, event: IServiceEvent) {
        e.stopPropagation();
        setOpenEvent(event);
        setShowModal(true);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function subServiceLinkClick(e: any) {
        e.stopPropagation();
        navigate(
            `/service/${props.parentService.id}/sub-service/${props.service.name}`
        );
    }

    const retryAll = async () => {
        infoToast("Starting retry");
        for (let index = 0; index < props.errors.length; index++) {
            const error = props.errors[index] as IServiceEvent;
            await RetryService(props.service.parentId, error.eventId);
        }
        infoToast("Retry finished, please refresh page/data");
    };

    return (
        <>
            <Card
                onClick={subServiceLinkClick}
                className="service-card cursor-pointer h-full mt-4 p-4"
                header={
                    <div className="flex justify-content-between align-items-center">
                        <div className="text-xl font-bold">
                            {props.service.name}
                        </div>
                        {props.errors && props.errors.length > 0 ? (
                            <Button 
                                onClick={retryAll} 
                                label="Retry all" 
                                icon="pi pi-sync"
                                className="p-button-pulsate"
                                style={{
                                    backgroundColor: 'var(--primary-color)',
                                    borderColor: 'var(--primary-color)',
                                    transition: 'all var(--transition-speed) ease'
                                }}
                            />
                        ) : null}
                    </div>
                }
                footer={
                    <EventCardFooter
                        errors={props.errors}
                        events={props.events}
                    />
                }
            >
                <div className="flex pt-1">
                    <div style={{ width: 50, minWidth: 50 }}>
                        <Image
                            src={props.parentService.icon}
                            width="50"
                            alt="Service image"
                        />
                    </div>

                    <div
                        style={{ minHeight: 50 }}
                        className="flex flex-grow-1 flex-wrap pl-2 md:pl-6 h-full align-items-center h-full"
                    >
                        {props.errors?.length > 0 ? (
                            <div className="w-full">
                                <p style={{ 
                                    color: "var(--error-color)",
                                    fontWeight: 600,
                                    fontSize: "1.1rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px"
                                }}>
                                    <i className="pi pi-exclamation-triangle" style={{ fontSize: "1rem" }}></i>
                                    Service is in error
                                </p>
                                {props.errors?.map((error, index) => {
                                    return (
                                        <div
                                            key={error.id}
                                            className="serviceCode mt-3 px-4 py-2"
                                            style={{
                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                                                background: "#240101",
                                                transition: "all var(--transition-speed) ease"
                                            }}
                                        >

                                        <div style={{ 
                                            display: "flex", 
                                            justifyContent: "space-between", 
                                            
                                            alignItems: "center",
                                            marginBottom: "10px" 
                                        }}>

                                            <div>
                                                <small style={{
                                                    fontWeight: 600,
                                                    color: "var(--primary-color)"
                                                }}>Error: {(index += 1)}</small>
                                            <code className="mt-3" key={error.id} style={{
                                                display: "block",
                                                fontFamily: "monospace",
                                                fontSize: "0.9rem",
                                                lineHeight: 1.5
                                            }}>
                                                {error.message}
                                            </code>
                                            </div>

                                            <Button
                                                icon="pi pi-search"
                                                className="p-button-rounded"
                                                onClick={(e) => openEventInDialog(e, error)}  
                                                tooltip="View details"
                                                tooltipOptions={{ position: 'top' }}
                                            />

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{ 
                                color: "var(--success-color)",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: "8px" 
                            }}>
                                <i className="pi pi-check-circle" style={{ fontSize: "1.1rem" }}></i>
                                <span>Service is operating successfully</span>
                            </div>
                        )}
                    </div>

                    <div
                        style={{ width: 100, marginTop: -10 }}
                        className="justify-content-end flex justify-content-center align-items-center"
                    >
                        {props.events ? (
                            <div
                                style={{
                                    backgroundColor:
                                        props.errors?.length > 0
                                            ? "var(--error-color)"
                                            : "var(--success-color)",
                                    width: 50,
                                    height: 50,
                                    borderRadius: "50%",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
                                    transition: "all var(--transition-speed) ease"
                                }}
                                className={`flex justify-content-center align-items-center status-indicator ${
                                    props.errors?.length > 0 ? "status-error" : "status-success"
                                }`}
                            >
                                <i
                                    className={`pi ${
                                        props.errors?.length > 0
                                            ? "pi-times"
                                            : "pi-check"
                                    }`}
                                    style={{ 
                                        fontSize: "1.7em", 
                                        color: "white",
                                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)" 
                                    }}
                                ></i>
                            </div>
                        ) : (
                            <Spinner width={50} height={50} />
                        )}
                    </div>
                </div>
            </Card>

            {openEvent ? (
                <ServiceActionModal
                    service={props.service}
                    event={openEvent}
                    visible={showModal}
                    onHide={() => setShowModal(false)}
                />
            ) : null}
        </>
    );
};
